export type CreativeActivity = {
  slug: string;
  emoji: string;
  title: string;
  intro: string;
  prompt: string;
  bullets?: string[];
  tone: string; // tailwind gradient-from class fragment
};

export const CREATIVE_ACTIVITIES: CreativeActivity[] = [
  {
    slug: "colour-your-feelings",
    emoji: "🎨",
    title: "Colour Your Feelings",
    intro: "Let colour speak for how you feel today.",
    prompt:
      "Choose colours that reflect how you feel right now and let them move across the page.",
    bullets: [
      "There is no right or wrong — only what feels true today.",
      "Try soft shapes, bold marks, or gentle scribbles.",
      "Notice which colours you keep returning to.",
    ],
    tone: "from-rose-100/50",
  },
  {
    slug: "safe-place",
    emoji: "🌳",
    title: "Draw Your Safe Place",
    intro: "Create a place that feels comforting, peaceful, or supportive.",
    prompt:
      "Draw a place where you feel completely safe and at peace.",
    bullets: [
      "What do you see?",
      "What colours are there?",
      "Is anyone with you?",
      "What makes this place feel safe?",
    ],
    tone: "from-emerald-100/50",
  },
  {
    slug: "weather-inside-me",
    emoji: "🌦️",
    title: "Weather Inside Me",
    intro: "If your inner world was weather today, what would it look like?",
    prompt:
      "If your emotions today were weather, what would they look like?",
    bullets: [
      "There is no right or wrong answer.",
      "Maybe it's sunshine, rain, fog, wind, a storm, or a rainbow.",
      "Express it however feels right.",
    ],
    tone: "from-sky-100/50",
  },
  {
    slug: "heart-mapping",
    emoji: "❤️",
    title: "Heart Mapping",
    intro: "Fill a heart with what supports you.",
    prompt:
      "Draw a heart and fill it with the people, places, memories, values, and things that help you feel supported.",
    bullets: [
      "Your heart can be colourful, simple, abstract, or completely unique.",
      "Add words, symbols, or small drawings inside.",
    ],
    tone: "from-pink-100/50",
  },
  {
    slug: "growth-tree",
    emoji: "🌱",
    title: "Growth Tree",
    intro: "Create a tree that reflects what's growing in you right now.",
    prompt:
      "Draw a tree that reflects who you are becoming.",
    bullets: [
      "Roots: strengths and supports that hold you.",
      "Branches: hopes, goals, and things you are learning.",
      "Leaves or fruit: what is beginning to bloom.",
    ],
    tone: "from-lime-100/50",
  },
  {
    slug: "scribble-transform",
    emoji: "✏️",
    title: "Scribble & Transform",
    intro: "Turn a random scribble into something meaningful.",
    prompt:
      "Start with a loose scribble, then transform it into something meaningful, creative, or playful.",
    bullets: [
      "Let your hand move freely before you decide anything.",
      "Look for shapes, faces, or images hiding in the marks.",
    ],
    tone: "from-violet-100/50",
  },

  {
    slug: "childhood-memory",
    emoji: "🖍️",
    title: "Draw a Childhood Memory",
    intro: "A memory from younger you — ordinary or vivid, whatever arrives.",
    prompt: "Draw a memory from when you were younger, exactly as you remember it.",
    bullets: [
      "It doesn't have to be a big moment.",
      "Colour matters more than accuracy.",
      "Stop whenever you've had enough.",
    ],
    tone: "from-amber-100/50",
  },
  {
    slug: "for-my-younger-self",
    emoji: "🎁",
    title: "Create Something for Your Younger Self",
    intro: "A gift, a place, a promise — something just for them.",
    prompt: "Make something for your younger self. What would they love to receive?",
    bullets: [
      "What would have delighted them?",
      "What would have made them feel safe?",
      "You can add words if you'd like to.",
    ],
    tone: "from-violet-100/50",
  },
];

export function findActivity(slug: string | undefined | null): CreativeActivity | null {
  if (!slug) return null;
  return CREATIVE_ACTIVITIES.find((a) => a.slug === slug) ?? null;
}
